/**
 * SocialButtons — shared "Continue with X" row for login/signup.
 *
 * Two providers live right now (Google + Magic Link). Microsoft / Apple /
 * GitHub scaffold ready but not yet exposed — see `_UPCOMING` and the
 * matching backend `_PROVIDERS` registry.
 *
 * Design rule (Oracle v1 baseline): answer "what should I click?" in <5s.
 * Two big buttons only, clearly labeled. No fine print.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { socialApi, startGoogleAuth, formatApiError } from "../../lib/authApi";
import VerifyInboxCard from "./VerifyInboxCard";

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 6 10-6"/>
  </svg>
);

/**
 * Two-column button row + inline magic-link email input.
 * The magic-link email input appears once the button is tapped.
 */
export const SocialButtons = ({ next = "/members", disabled = false }) => {
  const nav = useNavigate();
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(60);

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH. redirectUrl is derived from window.location inside
    // startGoogleAuth().
    startGoogleAuth({ next });
  };

  const onMagicSubmit = async (e) => {
    e.preventDefault();
    if (!magicEmail || busy) return;
    setBusy(true);
    try {
      const { data } = await socialApi.requestMagicLink(magicEmail, trustDevice);
      if (data?.throttled) {
        toast(`Please wait ${data.retry_after || 60}s before requesting another code.`);
      }
      setCooldownSeconds(data?.cooldown_seconds || 60);
      setSent(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send magic link.");
    } finally {
      setBusy(false);
    }
  };

  const onWrongEmail = () => {
    setSent(false);
    setMagicEmail("");
  };

  const onVerifiedSuccess = (data) => {
    // First-time signup (needs_profile_completion) → send them through
    // the callsign picker first, THEN drop them at the access-granted
    // splash. Everyone else lands on the cinematic splash directly.
    if (data?.needs_profile_completion) {
      nav(`/auth/finish-profile?next=${encodeURIComponent("/auth/access-granted?welcome=1")}`);
      return;
    }
    // Any sign-in that came from a signup mode gets the cinematic
    // first-time welcome (?welcome=1). Repeat sign-ins get the quiet
    // banner path (no ?welcome).
    const isFirst = data?.mode === "signup";
    nav(`/auth/access-granted${isFirst ? "?welcome=1" : ""}`);
  };

  if (sent) {
    return (
      <VerifyInboxCard
        email={magicEmail}
        rememberMe={trustDevice}
        cooldownSeconds={cooldownSeconds}
        onSuccess={onVerifiedSuccess}
        onWrongEmail={onWrongEmail}
      />
    );
  }

  return (
    <div className="mb-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={onGoogle}
          disabled={disabled}
          data-testid="social-google"
          className="flex items-center justify-center gap-2 px-3 py-2.5 border font-mono text-[11px] uppercase tracking-[0.25em] text-[#E4DFD3] hover:border-[#D92D20] disabled:opacity-50"
          style={{ borderColor: "#454038", background: "#08070A" }}
        >
          <GoogleIcon /> Google
        </button>
        <button
          type="button"
          onClick={() => setMagicMode((v) => !v)}
          disabled={disabled}
          data-testid="social-magic-toggle"
          className="flex items-center justify-center gap-2 px-3 py-2.5 border font-mono text-[11px] uppercase tracking-[0.25em] text-[#E4DFD3] hover:border-[#D92D20] disabled:opacity-50"
          style={{ borderColor: "#454038", background: "#08070A" }}
        >
          <MailIcon /> Magic link
        </button>
      </div>

      <p
        className="mt-2 font-mono text-[9px] uppercase tracking-[0.15em] text-[#6F6A60] leading-relaxed"
        data-testid="google-auth-notice"
      >
        Google verification briefly opens Emergent's secure authentication
        service. You will return to The Archive immediately after sign-in.
      </p>

      {magicMode && (
        <form onSubmit={onMagicSubmit} className="mt-3" data-testid="social-magic-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93]">
              EMAIL FOR SIGN-IN LINK
            </span>
            <input
              type="email"
              value={magicEmail}
              onChange={(e) => setMagicEmail(e.target.value)}
              required
              autoFocus
              data-testid="social-magic-email"
              className="mt-2 w-full bg-[#08070A] border px-3 py-2 text-sm text-[#E4DFD3] font-mono focus:outline-none focus:border-[#D92D20]"
              style={{ borderColor: "#454038" }}
            />
          </label>
          <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              data-testid="social-magic-trust-device"
              className="mt-0.5 accent-[#D92D20] shrink-0"
            />
            <span className="font-mono text-[10px] leading-[1.5] text-[#A39E93]">
              <span className="text-[#E4DFD3] uppercase tracking-[0.2em]">Trust this device for 90 days.</span>
              <br />
              <span className="text-[#6F6A60]">Do not use on shared or public computers.</span>
            </span>
          </label>
          <button
            type="submit"
            disabled={busy || !magicEmail}
            data-testid="social-magic-submit"
            className="mt-3 w-full font-mono text-[10px] uppercase tracking-[0.35em] py-2 border disabled:opacity-50"
            style={{ borderColor: "#D92D20", color: "#D92D20", background: "transparent" }}
          >
            {busy ? "SENDING…" : "SEND SIGN-IN LINK"}
          </button>
        </form>
      )}

      <Divider />
    </div>
  );
};

const Divider = () => (
  <div className="flex items-center gap-3 my-4" aria-hidden="true">
    <span className="flex-1 h-px" style={{ background: "#2D2A26" }} />
    <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#6F6A60]">or</span>
    <span className="flex-1 h-px" style={{ background: "#2D2A26" }} />
  </div>
);

export default SocialButtons;
